import React from "react";

const parseInline = (text: string, keyPrefix: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g).filter(Boolean);

  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return <strong key={`${keyPrefix}-strong-${index}`}>{part.slice(2, -2)}</strong>;
    }

    return <React.Fragment key={`${keyPrefix}-text-${index}`}>{part}</React.Fragment>;
  });
};

const renderParagraph = (lines: string[], key: string) => (
  <p key={key}>
    {lines.map((line, index) => (
      <React.Fragment key={`${key}-${index}`}>
        {index > 0 ? <br /> : null}
        {parseInline(line, `${key}-${index}`)}
      </React.Fragment>
    ))}
  </p>
);

interface FooterMarkdownProps {
  content: string;
}

const FooterMarkdown: React.FC<FooterMarkdownProps> = ({ content }) => {
  const normalizedLines = content.replace(/\r\n/g, "\n").split("\n");
  const elements: React.ReactNode[] = [];
  let paragraphBuffer: string[] = [];
  let listBuffer: string[] = [];

  const flushParagraph = () => {
    if (paragraphBuffer.length === 0) {
      return;
    }

    elements.push(renderParagraph(paragraphBuffer, `paragraph-${elements.length}`));
    paragraphBuffer = [];
  };

  const flushList = () => {
    if (listBuffer.length === 0) {
      return;
    }

    elements.push(
      <ul key={`list-${elements.length}`}>
        {listBuffer.map((item, index) => (
          <li key={`list-item-${index}`}>{parseInline(item, `list-item-${index}`)}</li>
        ))}
      </ul>
    );
    listBuffer = [];
  };

  normalizedLines.forEach((rawLine) => {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (!trimmed) {
      flushParagraph();
      flushList();
      return;
    }

    if (trimmed === "---") {
      flushParagraph();
      flushList();
      elements.push(<hr key={`hr-${elements.length}`} />);
      return;
    }

    if (trimmed.startsWith("- ")) {
      flushParagraph();
      listBuffer.push(trimmed.slice(2).trim());
      return;
    }

    flushList();

    const headingMatch = /^(#{1,3})\s+(.*)$/.exec(trimmed);
    if (headingMatch) {
      flushParagraph();
      const level = headingMatch[1].length;
      const text = headingMatch[2].trim();

      if (level === 1) {
        elements.push(<h1 key={`h1-${elements.length}`}>{parseInline(text, `h1-${elements.length}`)}</h1>);
      } else if (level === 2) {
        elements.push(<h2 key={`h2-${elements.length}`}>{parseInline(text, `h2-${elements.length}`)}</h2>);
      } else {
        elements.push(<h3 key={`h3-${elements.length}`}>{parseInline(text, `h3-${elements.length}`)}</h3>);
      }
      return;
    }

    paragraphBuffer.push(trimmed);
  });

  flushParagraph();
  flushList();

  return <div className="footer-markdown">{elements}</div>;
};

export default FooterMarkdown;
